/**
 * Centered, site-styled message boxes.
 *
 * The browser's own alert() and confirm() boxes are pinned to the top of the window
 * and look nothing like the rest of the site. These replace them with a centred card
 * in the site's own colours, so every message on every page looks the same.
 *
 * alert() is swapped out in place, so call sites do not change. confirm() cannot be:
 * the browser's version stops the page and hands back an answer straight away, which
 * no box of our own can do. Those call sites await appConfirm() instead.
 */

// Pages in the Tasks look (Permissions) get boxes in that look too.
const tasksLook = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/permissions');
const accent = () => '#2b6cf6'; // one blue on every page now
const font = () => (tasksLook() ? "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif" : 'Roboto, Helvetica, Arial, sans-serif');

let closeOpen: (() => void) | null = null;

const button = (label: string, kind: 'primary' | 'quiet') => {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.style.cssText = [
    kind === 'primary' ? `background:${accent()}` : 'background:transparent',
    kind === 'primary' ? 'color:#fff' : 'color:#475569',
    'border:0', `border-radius:${tasksLook() ? 11 : 8}px`,
    'padding:8px 22px', 'font-size:14px', `font-weight:${tasksLook() ? 500 : 600}`,
    'cursor:pointer', `font-family:${font()}`,
  ].join(';');
  return b;
};

/**
 * One card in the middle of the screen. `buttons` come back through `done`, and the
 * box closes itself first so nothing is left behind if the caller opens another.
 */
const card = (
  message: string,
  buttons: { label: string; kind: 'primary' | 'quiet'; value: any }[],
  escapeValue: any,
  done: (value: any) => void,
) => {
  if (closeOpen) closeOpen();

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:20000',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:16px', 'background:rgba(15,23,42,0.45)',
    'opacity:0', 'transition:opacity 120ms ease',
    `font-family:${font()}`,
  ].join(';');

  const box = document.createElement('div');
  box.style.cssText = [
    'background:#fff', `border-radius:${tasksLook() ? 20 : 12}px`, 'padding:24px',
    'max-width:440px', 'width:100%', 'box-sizing:border-box',
    'box-shadow:0 20px 60px rgba(0,0,0,0.25)',
    'text-align:left',
  ].join(';');

  const text = document.createElement('div');
  text.textContent = message;
  text.style.cssText = [
    `color:${tasksLook() ? '#1b2236' : '#1f2937'}`, `font-size:${tasksLook() ? 14 : 15}px`, 'font-weight:400', 'line-height:1.5',
    'white-space:pre-wrap', 'word-break:break-word',
    'margin-bottom:20px',
  ].join(';');

  const row = document.createElement('div');
  row.style.cssText = 'display:flex;justify-content:flex-end;gap:8px';

  let settled = false;
  const finish = (value: any) => {
    if (settled) return;
    settled = true;
    closeOpen = null;
    document.removeEventListener('keydown', onKey);
    overlay.style.opacity = '0';
    setTimeout(() => overlay.remove(), 120);
    done(value);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') finish(escapeValue);
    if (e.key === 'Enter') finish(buttons[buttons.length - 1].value);
  };

  let primary: HTMLButtonElement | null = null;
  buttons.forEach((b) => {
    const el = button(b.label, b.kind);
    el.onclick = () => finish(b.value);
    if (b.kind === 'primary') primary = el;
    row.appendChild(el);
  });

  box.appendChild(text);
  box.appendChild(row);
  overlay.appendChild(box);
  // Clicking the dark area behind the card is the same as backing out.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) finish(escapeValue); });

  document.body.appendChild(overlay);
  closeOpen = () => finish(escapeValue);
  requestAnimationFrame(() => { overlay.style.opacity = '1'; });
  if (primary) (primary as HTMLButtonElement).focus();
  document.addEventListener('keydown', onKey);
};

const showAlert = (message: string) => {
  card(message, [{ label: 'OK', kind: 'primary', value: true }], true, () => {});
};

/**
 * Ask a yes or no question. Use in place of window.confirm:
 *   if (!(await appConfirm('Delete this?'))) return;
 */
export const appConfirm = (
  message: string,
  options: { confirmLabel?: string; cancelLabel?: string } = {},
): Promise<boolean> => new Promise((resolve) => {
  card(
    message,
    [
      { label: options.cancelLabel || 'Cancel', kind: 'quiet', value: false },
      { label: options.confirmLabel || 'OK', kind: 'primary', value: true },
    ],
    false,
    (value) => resolve(Boolean(value)),
  );
});

/** Swap the browser box for ours. Safe to import more than once. */
export const installAppAlert = () => {
  if (typeof window === 'undefined' || (window as any).__appAlertInstalled) return;
  (window as any).__appAlertInstalled = true;
  window.alert = (message?: any) => showAlert(message == null ? '' : String(message));
};

installAppAlert();

export default installAppAlert;
